import { Component, Vue } from 'vue-property-decorator'

// ==============================
// === ドリル開始API

type EndCondition = {
  restriction: string
  left: number
}

// ドリル開始時の条件指定 パターンが増えた場合は拡張する
export type TargetCurriculum = {
  curriculumSUnitId?: number
}
// ドリル開始時のレスポンス
type ResponseStartDrill = {
  resultDrillId: number
  currentPage: number
}

// ==============================
// === 問題取得API

// 問題取得のレスポンス
export type ResponseStudyProblem = {
  currentPage: number
  subjectCode: string
  classModeCode: string
  classCategoryCode: string
  sProblemIds: number[] | null // 出題対象が存在しない場合はnull
  endConditions: EndCondition
  isFinished: boolean
}

// ==============================
// === 問題情報API

// 問題情報のレスポンス(1大問分)
export type ResponseProblem = {
  isLProblem: boolean
  subjectCode: string
  problems: SProblem[]
}

// 問題情報のレスポンス(1小問分)
type SProblem = {
  sProblemId: number
  sProblemCode: string
  number: number
  images: ImagePaths
  existMovie: boolean
  questions: ResponseQuestion[]
}

// 問題に対する画像パス群
export type ImagePaths = {
  d?: string[] // 読解文 配列で複数保持
  m?: string[] // 問題 配列で複数保持
  t?: string[] // 解答 配列で複数保持
  s?: string[] // 解説 配列で複数保持
  j?: string // 指示文 文字列で単体保持
}

// 問題情報のレスポンス(1設問分)
export type ResponseQuestion = {
  questionCode: string
  managementLabel: string | null
  timeLimit: number | null
  contents: ResponseQuestionContent[]
  images: ImagePaths
}

// 問題の解答情報レスポンス
//   解答タイプによって返ってくる値が変わる
type ResponseQuestionContent = {
  answerFormatCode: string
  labelText?: string | null
  beforeText?: string | null
  afterText?: string | null
  choiceText?: string | null
  sortNum?: number | null
  correctInputText?: string | null
  isCorrectText?: boolean | null
  correctSort?: number | null
}

// ==============================
// === 正誤入力情報取得API

// 正誤入力情報取得のレスポンス
export type ResponseResultProblem = {
  resultDrillId: number
  classModeCode: string
  classCategoryCode: string
  isCorrect: boolean
  pageNum: number
  problems: ResultProblem[]
  endConditions: EndCondition
  isFinished: boolean
}

// 大問実施結果
export type ResultProblem = {
  sProblemId: number
  sProblemCode: string
  isMoviePlayed?: boolean
  questions: ResultQuestion[]
}

// 設問実施結果
export type ResultQuestion = {
  questionCode: string
  correct: boolean | null
  userAnswerPath?: string
  answerFormatCode?: string
}

// ==============================
// === 動画取得API

// 解説動画取得リクエスト
type RequestProblemMovie = {
  sProblemId: number
  resultDrillId: number
  movieType: string
}

// 導入動画取得リクエスト
type RequestIntroductionMovie = {
  curriculumSUnitId: number
  movieType: string
}

// 動画取得結果
type ResponseProblemMovie = {
  movies: MovieData[]
  curriculumSUnitName: string
}

// 動画情報
export type MovieData = {
  movieId: number
  movieName: string
  movieFilename: string
  movieUrl: string
  sortNum: number
  liked: boolean
  watched: boolean
}

@Component
export default class WebApi extends Vue {
  /**
   * レスポンスコードから「削除済み」を判定する
   *
   * @param status ステータスコード(204)が削除済み
   * @returns 判定結果
   */
  protected statusDeleted(status: number): boolean {
    return status === 204
  }

  /**
   * ドリル開始Web API
   *
   * @param studyLogicType 学習ロジック('MN', 'AI')
   * @param classModeCode 授業モード
   * @param classCategoryCode ドリル種別
   * @param targetCurriculum 開始条件指定 内部の値はモードなどにより不定
   *
   * @returns ドリル結果を格納するテーブルとページ番号が返る
   */
  protected async startDrillApi(
    studyLogicType: string,
    classModeCode: string,
    classCategoryCode: string,
    targetCurriculum: TargetCurriculum
  ): Promise<ResponseStartDrill> {
    const params = {
      studyLogicType: studyLogicType,
      classModeCode: classModeCode,
      classCategoryCode: classCategoryCode,
      targetCurriculum: targetCurriculum,
    }

    Vue.prototype.$logger.info('-- WebApi startDrill')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.post('/v2/drills/start', params)
    return result.data
  }

  /**
   * ドリル完了API
   *  途中でドリルを終了する際に実行する
   *
   * @param resultDrillId ドリルID
   * @returns 処理結果ステータス
   */
  protected async completeDrillApi(resultDrillId: number, isModeAi?: boolean): Promise<number> {
    // パラメータ生成
    const params: { resultDrillId: number; stopType?: 'pause' } = {
      resultDrillId: resultDrillId,
    }
    if (isModeAi) {
      params.stopType = 'pause'
    }

    Vue.prototype.$logger.info('-- WebApi completeDrill')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.post('/v2/drills/complete', params)
    return result.status
  }

  /**
   * 問題取得API
   *  演習対象の問題を取得する(1大問分)
   *
   * @params resultDrillId ドリルID
   * @returns 出題情報
   */
  protected async getStudyProblems(resultDrillId: number): Promise<ResponseStudyProblem> {
    // パラメータ生成
    const params = {
      resultDrillId: resultDrillId,
    }

    Vue.prototype.$logger.info('-- WebApi getStudyProblems')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.get('/v2/studyProblems', {
      params: params,
    })
    return result.data
  }

  /**
   * 問題情報取得API
   *  画面表示に使用する問題情報を取得する(1大問)
   *
   * @param sProblemIds 小問IDの配列(大問単位)
   * @param isCorrect 正答情報を取得するか否か
   * @returns 画面表示用レスポンス(1件)
   */
  protected async getProblem(sProblemIds: number[], isCorrect = false): Promise<ResponseProblem> {
    const problems = await this.getProblems([sProblemIds], isCorrect)
    return problems[0]
  }

  /**
   * 問題情報取得API
   *  画面表示に使用する問題情報を取得する(複数大問)
   *
   * @param sProblemIds 小問IDの二次元配列(1項目は大問単位)
   * @param isCorrect 正答情報を取得するか否か
   * @returns 画面表示用レスポンス(複数)
   */
  protected async getProblems(sProblemIds: number[][], isCorrect = false): Promise<ResponseProblem[]> {
    // パラメータ生成
    const params = {
      sProblemIds: JSON.stringify(sProblemIds),
      isCorrect: isCorrect,
    }

    Vue.prototype.$logger.info('-- WebApi getProblems')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.get('/v2/problems', {
      params: params,
    })
    return result.data
  }

  /**
   * 正誤入力情報取得API
   *
   * @params resultDrillId ドリルID
   * @oarams page 対象ページ
   * @returns 正誤情報
   */
  protected async getResultProblem(resultDrillId: number, page: number): Promise<ResponseResultProblem> {
    // パラメータ生成
    const params = {
      resultDrillId: resultDrillId,
      pageNum: page,
    }

    Vue.prototype.$logger.info('-- WebApi getResultProblem')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.get('/v2/resultProblems', {
      params: params,
    })
    return result.data
  }

  /**
   * 正誤情報登録(ページ単位)API
   *
   * @params resultDrillId ドリルID
   * @params page 対象ページ
   * @params corrects 正誤情報
   * @returns 正誤情報
   */
  protected async registCorrectUnits(resultDrillId: number, page: number, corrects: (boolean | null)[]) {
    // パラメータ生成
    const params = {
      resultDrillId: resultDrillId,
      pageNum: page,
      corrects: corrects,
    }

    Vue.prototype.$logger.info('-- WebApi registCorrectUnits')
    Vue.prototype.$logger.info(params)

    await Vue.prototype.$http.httpWithToken.patch('/v2/resultProblems/unit', params)
  }

  /**
   * 解説動画取得
   *
   * @param sProblemId 問題ID
   * @param resultDrillId ドリルID
   * @returns 動画取得結果
   */
  protected async getExplainMovies(sProblemId: number, resultDrillId: number): Promise<ResponseProblemMovie> {
    // リクエスト定義(動画タイプは固定)
    const requestProblemMovie: RequestProblemMovie = {
      sProblemId: sProblemId,
      resultDrillId: resultDrillId,
      movieType: 'k',
    }

    Vue.prototype.$logger.info('-- WebApi getExplainMovies')
    Vue.prototype.$logger.info(requestProblemMovie)

    const result = await Vue.prototype.$http.httpWithToken.get('/v2/sProblemsMovies', {
      params: requestProblemMovie,
    })

    const resultData = {
      curriculumSUnitName: result.data.meta.curriculumSUnitName,
      movies: result.data.movies,
    }
    return resultData
  }

  /**
   * 導入動画取得
   *
   * @param curriculumSUnitId 小項目ID
   * @returns 動画取得結果
   */
  protected async getIntroductionMovies(curriculumSUnitId: number): Promise<ResponseProblemMovie> {
    // リクエスト定義(動画タイプは固定)
    const requestIntroductionMovie: RequestIntroductionMovie = {
      curriculumSUnitId: curriculumSUnitId,
      movieType: 'd',
    }

    Vue.prototype.$logger.info('-- WebApi getIntroductionMovies')
    Vue.prototype.$logger.info(requestIntroductionMovie)

    const result = await Vue.prototype.$http.httpWithToken.get('/v2/curriculumSUnitMovies', {
      params: requestIntroductionMovie,
    })

    const resultData = {
      curriculumSUnitName: result.data.meta.curriculumSUnitName,
      movies: result.data.movies,
    }
    return resultData
  }

  /**
   * s3の画像URLを取得
   *
   * @param path s3パス
   * @returns s3アクセス用フルパス
   */
  protected async getImageUrl(path: string): Promise<string> {
    const result = await Vue.prototype.$http.httpWithToken.get('/v2/imageUrl', { params: { path: path } })
    return result.data.url
  }
}
